import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type {
  Group,
  GroupMembershipAccepted,
  GroupMembershipInvited,
  GroupMembershipPotential,
  GroupMembershipRequested,
  IndividualMembership,
} from "@shared/models/group"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import type ModalManager from "cc-frontend/services/modal-manager"
import {
  getUserId,
  isGroupMembershipAccepted,
  isGroupMembershipInvited,
  isGroupMembershipPotential,
  isGroupMembershipRequested,
  membersWithAdminRole,
} from "cc-frontend/utils/group"
import { use } from "ember-could-get-used-to-this"
import { concat, find } from "lodash-es"
import * as R from "remeda" // tree-shaking supported!
import {
  ACTIONS_ACCEPT_COLUMN,
  ACTIONS_ALL_COLUMN,
  AVATAR_COLUMN,
  NAME_COLUMN,
  UPGRADED_ADMIN_COLUMN,
} from "../group/members-table"

interface Signature {
  Args: {
    group: Group
  }
}

export default class SchoolNetworkEditMembers extends Component<Signature> {
  @service declare modalManager: ModalManager

  membersTableColumns = [
    AVATAR_COLUMN,
    NAME_COLUMN,
    UPGRADED_ADMIN_COLUMN,
    ACTIONS_ACCEPT_COLUMN,
    ACTIONS_ALL_COLUMN,
  ]

  get allUserIds() {
    return R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(
        (membership) =>
          isGroupMembershipAccepted(membership) ||
          isGroupMembershipRequested(membership) ||
          isGroupMembershipPotential(membership)
      ),
      R.map(getUserId)
    )
  }

  @use userSummaries = new FindManyAltogether(() => ["user-summary", this.allUserIds])

  get adminMemberships() {
    let accepted = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithAdminRole),
      R.filter(isGroupMembershipAccepted),
      R.map(this.addSortNameByUser),
      R.compact,
      R.sortBy((membership) => membership.meta?.sortName)
    )
    let invited = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithAdminRole),
      R.filter(isGroupMembershipInvited),
      R.map(this.addSortNameByEmail),
      R.sortBy((membership) => membership.meta.sortName)
    )
    let requested = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithAdminRole),
      R.filter(isGroupMembershipRequested),
      R.map(this.addSortNameByUser),
      R.compact,
      R.sortBy((membership) => membership.meta?.sortName)
    )
    let potential = R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(membersWithAdminRole),
      R.filter(isGroupMembershipPotential),
      R.map(this.addSortNameByUser),
      R.compact,
      R.sortBy((membership) => membership.meta?.sortName)
    )

    return concat<IndividualMembership | GroupMembershipRequested | GroupMembershipPotential>(
      accepted,
      invited,
      requested,
      potential
    )
  }

  @action addSortNameByUser<
    M extends GroupMembershipAccepted | GroupMembershipRequested | GroupMembershipPotential
  >(membership: M): M | null {
    if (this.userSummaries.content === null) return null
    let user = find(
      this.userSummaries.content,
      (summary) => summary.id === membership.attributes.userId
    )
    if (user === undefined) return null
    let sortName = get(user.attributes, "lastName") ?? ""
    membership.meta = { sortName: sortName }
    return membership
  }

  @action addSortNameByEmail(membership: GroupMembershipInvited): GroupMembershipInvited {
    let sortName = get(membership.attributes, "email") ?? ""
    membership.meta = { sortName: sortName }
    return membership
  }

  @action
  addMember(group) {
    this.modalManager.openModal("group/add-members", {
      group: group,
      showOnlyAdmins: true,
      makeAdmin: true,
      parentGroup: null,
    })
  }
}
